import React, { useCallback, useState } from "react";
import { Field } from "react-final-form";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import RffInputWrapper from "../shared/RffInputWrapper";

/**
 * RffSsnField — masked SSN entry. The form value is always the bare 9 digits;
 * the dashes are display-only, and only rendered while the value is revealed
 * (a password field padded with dashes would misreport its own length).
 */

/**
 * Structural rules the SSA has never issued against. Not a lookup — it only
 * catches numbers that cannot exist, which is the class of typo worth catching
 * before a credit pull fails on it.
 */
const NEVER_ISSUED_AREAS = ["000", "666"];

const ssnStructureError = (digits) => {
  const area = digits.slice(0, 3);
  const group = digits.slice(3, 5);
  const serial = digits.slice(5);
  if (NEVER_ISSUED_AREAS.includes(area) || area >= "900") return true;
  if (group === "00") return true;
  if (serial === "0000") return true;
  return false;
};
const RffSsnField = ({
  name,
  label,
  required = false,
  helperText,
  fullWidth = true,
  row = false,
  suppressGrid,
  size,
  disabled,
  onBlur,
}) => {
  const [showSsn, setShowSsn] = useState(false);

  // undefined, not "", when empty — an empty string counts as "present" to the
  // step-completion checks and would ride along in a submitted payload.
  const parseSsn = useCallback((value) => {
    if (!value) return undefined;
    const digits = value.replace(/[^\d]/g, "").slice(0, 9);
    return digits || undefined;
  }, []);

  const formatSsn = useCallback(
    (value) => {
      if (!value) return "";
      const digits = value.replace(/[^\d]/g, "");
      if (!showSsn) return digits;
      if (digits.length <= 3) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    },
    [showSsn],
  );

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      helperText={helperText}
      required={required}
      row={row}
    >
      <Field
        name={name}
        parse={parseSsn}
        format={formatSsn}
        validate={(v) => {
          const digits = v ? String(v).replace(/[^\d]/g, "") : "";
          if (!digits) return required ? "This field is required" : undefined;
          // Worth its own message: the input is masked by default, so "too
          // short" is the one mistake the applicant cannot see for themselves.
          if (digits.length < 9) {
            return `Enter all 9 digits (${digits.length} so far)`;
          }
          if (ssnStructureError(digits)) {
            return "Enter a valid Social Security Number";
          }
          return undefined;
        }}
        render={({ input, meta }) => (
          <TextField
            {...input}
            onBlur={onBlur || input.onBlur}
            type={showSsn ? "text" : "password"}
            disabled={disabled}
            fullWidth={fullWidth}
            placeholder="000-00-0000"
            error={meta.touched && !!meta.error}
            helperText={meta.touched && meta.error ? meta.error : null}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={showSsn ? "Hide SSN" : "Show SSN"}
                    onClick={() => setShowSsn((prev) => !prev)}
                  >
                    {showSsn ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </RffInputWrapper>
  );
};

export default RffSsnField;
